'use client';

import { MediaRef } from "@/app/__to_be_integrated/_____components/image/enhanced-image/types/types";
import { Paper } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import Image from "next/image";
import ImageGalleryNavigation from "@/app/features/shared/media/image-gallery/v1/ui/navigation";
import Counter from "@/app/features/shared/media/image-gallery/v1/ui/counter";
import { useTheme } from "@mui/material/styles";
import {
    mediaRefAlt,
    mediaRefDimensions,
    mediaRefName,
    mediaRefUrl
} from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import DetailsMenu from "@/app/features/shared/media-view/ui/shared/DetailsMenu";
import MetaOverlay from "@/app/features/shared/media-view/ui/shared/MetaOverlay";
import MagnifierGlass from "@/app/features/shared/media-view/ui/shared/MagnifierGlass";
import { resolveObjectPositionOffsets } from "@/app/features/shared/media-view/helper/magnifier-helper";

interface ImageGalleryProps {
    images: MediaRef[];
}

type LayerId = 'a' | 'b';

interface GalleryThumbnailProps {
    image: MediaRef;
    index: number;
    isActive: boolean;
    onSelect: (index: number) => void;
    activeThumbnailRef: RefObject<HTMLButtonElement | null>;
    activeOutlineColor: string;
}

interface ActiveMediaLayerViewProps {
    image: MediaRef;
    isVisible: boolean;
    shouldPreload: boolean;
    zIndex: number;
    aspectRatio: number;
    layerId: LayerId;
    onLayerLoad: (layer: LayerId) => void;
}

const IMAGE_FADE_DURATION_MS = 120;
const IMAGE_FADE_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const DEFAULT_ASPECT_RATIO = 1080 / 900;
const MAIN_IMAGE_SIZES = '(max-width: 600px) 100vw, (max-width: 1200px) 50vw, 33vw';
const MAGNIFIER_SIZE = 220;
const MAGNIFIER_ZOOM_LEVEL = 2.2;
const ACTIVE_METADATA_OPTIONS = {
    showName: true,
    showCaption: true,
} as const;

function getMediaAspectRatio(media: MediaRef | undefined): number {
    const dimensions = mediaRefDimensions(media);
    if (dimensions?.width && dimensions?.height) {
        return dimensions.width / dimensions.height;
    }

    return DEFAULT_ASPECT_RATIO;
}

function stepIndex(currentIndex: number, delta: 1 | -1, length: number): number {
    if (length <= 0) return 0;
    if (delta < 0) return currentIndex === 0 ? length - 1 : currentIndex - 1;
    return currentIndex === length - 1 ? 0 : currentIndex + 1;
}

export default function ImageGallery({ images }: ImageGalleryProps) {

    const theme = useTheme();
    const imagesLength = images.length;
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [visibleLayer, setVisibleLayer] = useState<LayerId>('a');
    const [layerAIndex, setLayerAIndex] = useState<number>(0);
    const [layerBIndex, setLayerBIndex] = useState<number>(0);
    const [transitionTargetIndex, setTransitionTargetIndex] = useState<number | null>(null);
    const activeThumbnailRef = useRef<HTMLButtonElement>(null);
    const displayedIndex = visibleLayer === 'a' ? layerAIndex : layerBIndex;
    const hiddenLayer: LayerId = visibleLayer === 'a' ? 'b' : 'a';
    const layerAAspectRatio = useMemo(() => getMediaAspectRatio(images[layerAIndex]), [images, layerAIndex]);
    const layerBAspectRatio = useMemo(() => getMediaAspectRatio(images[layerBIndex]), [images, layerBIndex]);
    const preloadCandidates = useMemo(() => {
        return images.map((image, index) => {
            const dimensions = mediaRefDimensions(image);
            const fullImageUrl = mediaRefUrl(image);
            return {
                key: `${index}-${fullImageUrl}`,
                src: fullImageUrl,
                width: dimensions?.width ?? 1080,
                height: dimensions?.height ?? 900,
            };
        });
    }, [images]);

    useEffect(() => {
        if (imagesLength === 0) return;

        const maxIndex = imagesLength - 1;
        setActiveIndex((previousIndex) => Math.min(previousIndex, maxIndex));
        setLayerAIndex((previousIndex) => Math.min(previousIndex, maxIndex));
        setLayerBIndex((previousIndex) => Math.min(previousIndex, maxIndex));
    }, [imagesLength]);

    useEffect(() => {
        if (activeThumbnailRef.current) {
            activeThumbnailRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
        }
    }, [activeIndex]);

    useEffect(() => {
        if (activeIndex === displayedIndex) {
            setTransitionTargetIndex(null);
            return;
        }

        setTransitionTargetIndex(activeIndex);
        if (hiddenLayer === 'a') {
            setLayerAIndex(activeIndex);
            return;
        }

        setLayerBIndex(activeIndex);
    }, [activeIndex, displayedIndex, hiddenLayer]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (imagesLength === 0) return;
            switch (event.key) {
                case 'ArrowLeft':
                    setActiveIndex((previousIndex) => stepIndex(previousIndex, -1, imagesLength));
                    break;
                case 'ArrowRight':
                    setActiveIndex((previousIndex) => stepIndex(previousIndex, 1, imagesLength));
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [imagesLength]);

    const onThumbnailClick = useCallback((index: number) => {
        setActiveIndex((previousIndex) => previousIndex === index ? previousIndex : index);
    }, []);

    const setPreviousIndex = useCallback(() => {
        if (imagesLength === 0) return;
        setActiveIndex((previousIndex) => stepIndex(previousIndex, -1, imagesLength));
    }, [imagesLength]);

    const setNextIndex = useCallback(() => {
        if (imagesLength === 0) return;
        setActiveIndex((previousIndex) => stepIndex(previousIndex, 1, imagesLength));
    }, [imagesLength]);

    const onLayerLoad = useCallback((layer: LayerId) => {
        if (transitionTargetIndex === null) return;
        const currentHiddenLayer: LayerId = visibleLayer === 'a' ? 'b' : 'a';
        if (layer !== currentHiddenLayer) return;
        const layerIndex = layer === 'a' ? layerAIndex : layerBIndex;
        if (layerIndex !== transitionTargetIndex || layerIndex !== activeIndex) return;
        setVisibleLayer(layer);
        setTransitionTargetIndex(null);
    }, [activeIndex, layerAIndex, layerBIndex, transitionTargetIndex, visibleLayer]);

    const isLayerAVisible = visibleLayer === 'a';
    const isLayerBVisible = !isLayerAVisible;

    if (imagesLength === 0) {
        return null;
    }

    return (
        <Paper className="relative h-[95vh] w-[95vw] max-h-[95vh] max-w-[95vw] p-2">
            <div className="flex h-full w-full flex-col gap-2 overflow-hidden">
                <div className="relative flex h-[80%] w-full max-h-[80%] max-w-full min-h-0 min-w-0 items-center justify-center">
                    <ActiveMediaLayerView
                        image={images[layerAIndex]}
                        isVisible={isLayerAVisible}
                        shouldPreload={transitionTargetIndex !== null && !isLayerAVisible}
                        zIndex={1}
                        aspectRatio={layerAAspectRatio}
                        layerId="a"
                        onLayerLoad={onLayerLoad}
                    />
                    <ActiveMediaLayerView
                        image={images[layerBIndex]}
                        isVisible={isLayerBVisible}
                        shouldPreload={transitionTargetIndex !== null && !isLayerBVisible}
                        zIndex={2}
                        aspectRatio={layerBAspectRatio}
                        layerId="b"
                        onLayerLoad={onLayerLoad}
                    />
                </div>
                <Counter current={activeIndex} total={images.length}/>
                <ImageGalleryNavigation onPrevious={setPreviousIndex} onNext={setNextIndex}/>
                <div className="flex h-[20%] w-auto max-h-[20%] justify-center overflow-x-scroll overflow-y-hidden">
                    {images.map((image, index) => (
                        <GalleryThumbnail
                            key={index}
                            image={image}
                            index={index}
                            isActive={index === activeIndex}
                            onSelect={onThumbnailClick}
                            activeThumbnailRef={activeThumbnailRef}
                            activeOutlineColor={theme.palette.primary.main}
                        />
                    ))}
                </div>
            </div>
            <div className="pointer-events-none absolute -z-10 h-0 w-0 overflow-hidden opacity-0" aria-hidden="true">
                {preloadCandidates.map((candidate, index) => (
                    <Image
                        key={candidate.key}
                        src={candidate.src}
                        alt=""
                        width={candidate.width}
                        height={candidate.height}
                        sizes={MAIN_IMAGE_SIZES}
                        loading="eager"
                        fetchPriority={index < 2 ? 'high' : 'auto'}
                        decoding="async"
                    />
                ))}
            </div>
        </Paper>
    )
}

const GalleryThumbnail = memo(function GalleryThumbnail({
    image,
    index,
    isActive,
    onSelect,
    activeThumbnailRef,
    activeOutlineColor,
}: GalleryThumbnailProps) {
    return (
        <button
            type="button"
            className="thumbnail-item flex min-h-0 flex-none cursor-pointer border-0 bg-transparent p-2"
            onClick={() => onSelect(index)}
            ref={isActive ? activeThumbnailRef : undefined}
            aria-label={`Bild ${index + 1} anzeigen${mediaRefName(image) ? `: ${mediaRefName(image)}` : ''}`}
            aria-current={isActive ? "true" : undefined}
        >
            <div className="relative w-[250px] h-full min-h-0">
                <Image
                    src={mediaRefUrl(image, { size: 'thumbnail' })}
                    alt={mediaRefAlt(image) ?? ''}
                    fill
                    sizes="250px"
                    loading="lazy"
                    decoding="async"
                    style={{
                        objectFit: "cover",
                    }}
                />
                <div
                    className="pointer-events-none absolute inset-0 border-[3px] transition-opacity duration-150 ease-out"
                    style={{
                        borderColor: activeOutlineColor,
                        opacity: isActive ? 1 : 0,
                    }}
                />
            </div>
        </button>
    );
}, (previousProps, nextProps) => {
    return previousProps.image === nextProps.image
        && previousProps.isActive === nextProps.isActive
        && previousProps.activeOutlineColor === nextProps.activeOutlineColor;
});

// TODO: optimize by build an lightweight media view
const ActiveMediaLayerView = memo(function ActiveMediaLayerView({
    image,
    isVisible,
    shouldPreload,
    zIndex,
    aspectRatio,
    layerId,
    onLayerLoad,
}: ActiveMediaLayerViewProps) {
    const [isMagnifying, setIsMagnifying] = useState(false);
    const [showMagnifierGlass, setShowMagnifierGlass] = useState(false);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const imageContainerRef = useRef<HTMLDivElement | null>(null);
    const frameRef = useRef<number | null>(null);
    const glassRef = useRef<HTMLDivElement | null>(null);
    const pendingPointerRef = useRef<{ x: number; y: number } | null>(null);
    const layoutRef = useRef<{
        width: number;
        height: number;
        scaledW: number;
        scaledH: number;
        offsetX: number;
        offsetY: number;
    } | null>(null);

    useEffect(() => {
        setIsMagnifying(false);
        setShowMagnifierGlass(false);
        setImageSize({ width: 0, height: 0 });
        layoutRef.current = null;
        pendingPointerRef.current = null;
        if (frameRef.current !== null && typeof window !== 'undefined') {
            window.cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
    }, [image]);

    useEffect(() => {
        if (!isVisible) {
            setIsMagnifying(false);
            setShowMagnifierGlass(false);
        }
    }, [isVisible]);

    const applyMagnifierStyles = useCallback((mouseX: number, mouseY: number, bgX: number, bgY: number) => {
        const glass = glassRef.current;
        if (!glass) return;
        glass.style.setProperty('--media-view-magnifier-glass-left', `${mouseX - (MAGNIFIER_SIZE / 2)}px`);
        glass.style.setProperty('--media-view-magnifier-glass-top', `${mouseY - (MAGNIFIER_SIZE / 2)}px`);
        glass.style.setProperty('--media-view-magnifier-bg-x', `${bgX}px`);
        glass.style.setProperty('--media-view-magnifier-bg-y', `${bgY}px`);
    }, []);

    const measureLayout = useCallback((containerEl: HTMLElement) => {
        const imageEl = containerEl.querySelector('img') as HTMLImageElement | null;
        if (!imageEl) return null;

        const containerRect = containerEl.getBoundingClientRect();
        const computed = getComputedStyle(imageEl);
        const naturalW = imageEl.naturalWidth || 1;
        const naturalH = imageEl.naturalHeight || 1;

        const { scaledW, scaledH, offsetX, offsetY } = resolveObjectPositionOffsets(
            computed,
            containerRect.width,
            containerRect.height,
            naturalW,
            naturalH,
        );

        const nextLayout = {
            width: containerRect.width,
            height: containerRect.height,
            scaledW,
            scaledH,
            offsetX,
            offsetY,
        };

        layoutRef.current = nextLayout;
        setImageSize((previous) => {
            if (Math.abs(previous.width - scaledW) < 0.5 && Math.abs(previous.height - scaledH) < 0.5) {
                return previous;
            }

            return { width: scaledW, height: scaledH };
        });

        return nextLayout;
    }, []);

    const updateMagnifierPosition = useCallback((clientX: number, clientY: number) => {
        const containerEl = imageContainerRef.current;
        if (!containerEl) return;

        const containerRect = containerEl.getBoundingClientRect();
        const currentLayout = layoutRef.current;
        const sizeChanged = !currentLayout
            || Math.abs(currentLayout.width - containerRect.width) > 0.5
            || Math.abs(currentLayout.height - containerRect.height) > 0.5;
        const layout = sizeChanged ? measureLayout(containerEl) : currentLayout;
        if (!layout) return;

        const mouseX = clientX - containerRect.left;
        const mouseY = clientY - containerRect.top;
        const relX = mouseX + layout.offsetX;
        const relY = mouseY + layout.offsetY;

        applyMagnifierStyles(
            mouseX,
            mouseY,
            -relX * MAGNIFIER_ZOOM_LEVEL + (MAGNIFIER_SIZE / 2),
            -relY * MAGNIFIER_ZOOM_LEVEL + (MAGNIFIER_SIZE / 2),
        );
    }, [applyMagnifierStyles, measureLayout]);

    const flushMagnifierUpdate = useCallback(() => {
        frameRef.current = null;
        const pointer = pendingPointerRef.current;
        if (!pointer) return;
        updateMagnifierPosition(pointer.x, pointer.y);
    }, [updateMagnifierPosition]);

    const queueMagnifierUpdate = useCallback((x: number, y: number) => {
        pendingPointerRef.current = { x, y };
        if (typeof window === 'undefined') return;
        if (frameRef.current !== null) return;
        frameRef.current = window.requestAnimationFrame(flushMagnifierUpdate);
    }, [flushMagnifierUpdate]);

    const onMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!isVisible || !isMagnifying) return;
        queueMagnifierUpdate(event.clientX, event.clientY);
    }, [isMagnifying, isVisible, queueMagnifierUpdate]);

    const onMouseEnter = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
        if (!isVisible || !isMagnifying) return;
        setShowMagnifierGlass(true);
        queueMagnifierUpdate(event.clientX, event.clientY);
    }, [isMagnifying, isVisible, queueMagnifierUpdate]);

    const onMouseLeave = useCallback(() => {
        setShowMagnifierGlass(false);
    }, []);

    const onImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
        onLayerLoad(layerId);
        const nextImage = event.currentTarget;
        setImageSize((previous) => {
            if (Math.abs(previous.width - nextImage.naturalWidth) < 0.5 && Math.abs(previous.height - nextImage.naturalHeight) < 0.5) {
                return previous;
            }

            return {
                width: nextImage.naturalWidth,
                height: nextImage.naturalHeight,
            };
        });
        const containerEl = imageContainerRef.current;
        if (containerEl) {
            measureLayout(containerEl);
        }
    }, [layerId, measureLayout, onLayerLoad]);

    return (
        <div
            className="absolute inset-0 flex items-center justify-center"
            style={{
                zIndex,
                opacity: isVisible ? 1 : 0,
                pointerEvents: isVisible ? 'auto' : 'none',
                transition: `opacity ${IMAGE_FADE_DURATION_MS}ms ${IMAGE_FADE_EASING}`,
                willChange: 'opacity',
            }}
        >
            <div
                className="relative flex h-full max-h-full w-auto max-w-full min-h-0 min-w-0"
                style={{ aspectRatio }}
            >
                <div
                    ref={imageContainerRef}
                    className="relative h-full w-full min-h-0 min-w-0"
                    onMouseMove={onMouseMove}
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                >
                    <Image
                        src={mediaRefUrl(image)}
                        alt={mediaRefAlt(image) ?? ''}
                        fill
                        sizes={MAIN_IMAGE_SIZES}
                        loading={shouldPreload ? 'eager' : 'lazy'}
                        fetchPriority={shouldPreload ? 'high' : 'auto'}
                        decoding="async"
                        onLoad={onImageLoad}
                        style={{
                            objectFit: "contain",
                        }}
                    />
                    {isVisible && showMagnifierGlass && isMagnifying && (
                        <MagnifierGlass
                            image={image}
                            imageSize={imageSize}
                            zoomLevel={MAGNIFIER_ZOOM_LEVEL}
                            size={MAGNIFIER_SIZE}
                            glassRef={glassRef}
                        />
                    )}
                    {isVisible && (
                        <DetailsMenu
                            position="top right"
                            magnifierEnabled
                            isMagnifying={isMagnifying}
                            setIsMagnifying={setIsMagnifying}
                        />
                    )}
                    {isVisible && (
                        <MetaOverlay
                            src={image}
                            options={ACTIVE_METADATA_OPTIONS}
                        />
                    )}
                </div>
            </div>
        </div>
    );
});
